import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import type { SourceType } from '@trello/analytics-types';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { intl } from '@trello/i18n';
import { useBoardId, useCardId, useListId } from '@trello/id-context';
import { showFlag } from '@trello/nachos/experimental-flags';
import { useMemberInboxIds } from '@trello/personal-workspace';
import { useSharedStateSelector } from '@trello/shared-state';

import { useCardMove } from 'app/src/components/BoardListView/useCardMove';
import { inboxSelectedCardsSharedState } from 'app/src/components/Inbox/inboxSelectedCardsSharedState';
import { BoardListPositionSelect } from './BoardListPositionSelect';
import { CopyMoveCardTabs } from './CopyMoveCardTabs';
import { useCurrentCardAttributesFragment } from './CurrentCardFragment.generated';
import { InboxPositionSelector } from './InboxPositionSelector';
import { SuggestedMoves } from './SuggestedMoves';
import { useBulkCardMove } from './useBulkCardMove';

import * as styles from './MoveCardPopover.module.less';

export interface MoveCardPopoverProps {
  onClose: () => void;
  source?: SourceType;
}

export const MoveCardPopover: FunctionComponent<MoveCardPopoverProps> = ({
  onClose,
  source,
}) => {
  const cardId = useCardId();
  const boardId = useBoardId();
  const listId = useListId();

  const { idBoard: inboxBoardId, idList: inboxListId } = useMemberInboxIds();

  const { data: currentCardAttributesData } = useCurrentCardAttributesFragment({
    from: { id: cardId },
  });

  const moveCardInlineDialog = 'moveCardInlineDialog';
  const moveCardSource = source || moveCardInlineDialog;

  useEffect(() => {
    Analytics.sendScreenEvent({
      name: moveCardInlineDialog,
      containers: formatContainers({ boardId, cardId, listId }),
      attributes: {
        cardIsTemplate: currentCardAttributesData?.isTemplate,
        cardIsClosed: currentCardAttributesData?.closed,
        isModernizedMoveCardPopover: true,
      },
    });
  }, [
    boardId,
    cardId,
    listId,
    currentCardAttributesData?.isTemplate,
    currentCardAttributesData?.closed,
    currentCardAttributesData?.cardRole,
  ]);

  /**
   * This is so that the first suggestions button or the first select field
   * is focused when the popover is opened, otherwise it only focuses when
   * first opened, and not again until the page is refreshed.
   * This can probably be revisited when the popover mechanism is switched to the
   * modernized version.
   */
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const popoverElement = ref.current;
    if (popoverElement) {
      const focusableElements = popoverElement.querySelectorAll(
        'button, input, [tabindex]:not([tabindex="-1"])',
      );
      const firstElement = focusableElements[0];
      if (firstElement) {
        const timeoutId = setTimeout(
          () => (firstElement as HTMLElement).focus(),
          1,
        );
        return () => clearTimeout(timeoutId);
      }
    }
  }, []);

  const { moveCard } = useCardMove();

  const { bulkMoveCards } = useBulkCardMove();

  const inboxSelectedCards = useSharedStateSelector(
    inboxSelectedCardsSharedState,
    useCallback((state: Set<string>) => {
      return Array.from(state);
    }, []),
  );

  const onInboxMove = useCallback(
    async (index: number) => {
      try {
        if (inboxSelectedCards.length > 0) {
          await bulkMoveCards({
            cardIds: inboxSelectedCards,
            idBoard: boardId,
            posIndex: index,
            targetBoardId: inboxBoardId!,
            targetListId: inboxListId!,
            source,
          });
          inboxSelectedCardsSharedState.setValue(new Set());
        } else {
          await moveCard({
            boardId: inboxBoardId,
            listId: inboxListId!,
            cardId,
            index,
          });
        }
      } catch (err) {
        showFlag({
          appearance: 'error',
          id: 'card-move-popover-move-submit',
          isAutoDismiss: true,
          title: intl.formatMessage({
            id: 'templates.popover_move_card.could-not-move-card',
            defaultMessage: 'Moving card failed',
            description: 'Moving card failed error flag message',
          }),
        });
        sendErrorEvent(err, {
          tags: {
            ownershipArea: 'trello-enterprise',
            feature: 'Card Move Inbox Tab',
          },
        });
      }
      onClose();
    },
    [
      onClose,
      inboxSelectedCards,
      bulkMoveCards,
      boardId,
      inboxBoardId,
      inboxListId,
      source,
      moveCard,
      cardId,
    ],
  );

  return (
    <div ref={ref}>
      <div className={styles.popoverSection}>
        <CopyMoveCardTabs
          generateInboxTab={() => (
            <InboxPositionSelector variation="move" onAction={onInboxMove} />
          )}
          generateBoardTab={() => (
            <>
              <SuggestedMoves onClose={onClose} source={moveCardSource} />
              <h2 className={styles.header}>
                <FormattedMessage
                  id="templates.popover_move_card.select-destination"
                  defaultMessage="Select destination"
                  description="Select destination header on move card popover"
                />
              </h2>
              <BoardListPositionSelect
                isMove={true}
                onClose={onClose}
                source={moveCardSource}
              />
            </>
          )}
        />
      </div>
    </div>
  );
};
